import { FC } from 'react';
import { Avatar, Button, Col, Divider, Row, Space, Switch, Typography } from 'antd';
import {
    TbArrowUpRight,
    TbCloudLock,
    TbFolderBolt,
    TbInfoCircle,
    TbSitemap,
    TbVocabulary,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { WorkspaceIdentityInput, WorkspaceIdentityPortalLinkInput } from '@/services/database';
import { EmptySection, EnrichedAnchorTag, OurDocsLink, SmallTooltip } from '@/shared';
import { WorkspaceIdentity, WorkspaceIdentityDirectory } from '@/types';
import dayjs from '@/utilities/dayjs';

import AdvancedSettingsSectionHeader from '../../../components/AdvancedSettingsSectionHeader';

interface DirectorySyncSectionProps {
    identity: WorkspaceIdentity | null;
    isEnterprise: boolean;
    openingPortalIntent: WorkspaceIdentityPortalLinkInput['intent'] | null;
    openWorkOSPortal: (intent: WorkspaceIdentityPortalLinkInput['intent']) => void;
    saving: boolean;
    updateIdentity: (input: WorkspaceIdentityInput, toastLabel: string) => void;
}

const directoryStateLabel = (directory: WorkspaceIdentityDirectory) => {
    if (!directory.state) {
        return 'Unknown';
    }

    return directory.state
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
};

const DirectorySyncSection: FC<DirectorySyncSectionProps> = ({
    identity,
    isEnterprise,
    openingPortalIntent,
    openWorkOSPortal,
    saving,
    updateIdentity,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <>
            <AdvancedSettingsSectionHeader
                description='Provision and deprovision workspace members from your identity provider (IdP).'
                icon={<TbVocabulary size='1rem' />}
                plan='enterprise'
                title={
                    <>
                        SCIM & Directory Sync
                        <SmallTooltip
                            placement='right'
                            padding='0 0.5rem'
                            title={
                                <span>
                                    We use <EnrichedAnchorTag label={<>WorkOS</>} url='https://workos.com' /> for
                                    enterprise directory provisioning. Learn more in <OurDocsLink />
                                </span>
                            }
                        >
                            <small style={{ fontWeight: 'normal' }}>
                                <TbInfoCircle
                                    style={{
                                        color: token.colorTextTertiary,
                                        marginLeft: '0.625rem',
                                        verticalAlign: '-0.1rem',
                                    }}
                                />
                            </small>
                        </SmallTooltip>
                    </>
                }
                extra={
                    <Switch
                        checked={Boolean(identity?.scimEnabled)}
                        disabled={!isEnterprise || saving}
                        loading={saving}
                        onChange={(checked) =>
                            updateIdentity(
                                { scimEnabled: checked },
                                checked ? 'Enabled directory sync' : 'Disabled directory sync',
                            )
                        }
                    />
                }
            />
            {identity?.scimEnabled && (
                <section
                    style={{
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        marginTop: '1rem',
                        padding: '1rem',
                    }}
                >
                    <Row align='middle' gutter={[12, 12]} justify='space-between' wrap>
                        <Col>
                            <Space size={8}>
                                <Avatar
                                    icon={<TbFolderBolt size='1rem' />}
                                    shape='square'
                                    size={24}
                                    style={{
                                        background: token.colorBgElevated,
                                        border: `1px solid ${token.colorBorder}`,
                                        color: token.colorTextTertiary,
                                    }}
                                />
                                <div>
                                    <Typography.Text strong>WorkOS Directory Sync</Typography.Text>
                                    <div>
                                        <small style={{ color: token.colorTextTertiary }}>
                                            {identity.workOSOrganizationId
                                                ? `Organization ${identity.workOSOrganizationId}`
                                                : 'No WorkOS organization has been created yet.'}
                                        </small>
                                    </div>
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Space.Compact>
                                <Button
                                    disabled={!isEnterprise}
                                    loading={openingPortalIntent === 'dsync'}
                                    onClick={() => openWorkOSPortal('dsync')}
                                >
                                    <TbFolderBolt
                                        style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                                    />
                                    Configure Directory Sync
                                    <TbArrowUpRight
                                        style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                                    />
                                </Button>
                                <Button
                                    disabled={!isEnterprise}
                                    loading={openingPortalIntent === 'sso'}
                                    onClick={() => openWorkOSPortal('sso')}
                                >
                                    <TbCloudLock
                                        style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                                    />
                                    Configure SAML SSO
                                    <TbArrowUpRight
                                        style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                                    />
                                </Button>
                            </Space.Compact>
                        </Col>
                    </Row>
                    <Divider style={{ margin: '1rem 0' }} />
                    {!identity.workOSAvailable && (
                        <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: '1rem' }}>
                            {identity.workOSError}
                        </Typography.Paragraph>
                    )}
                    {identity.directories.length > 0 ? (
                        <Row gutter={[12, 12]}>
                            {identity.directories.map((directory) => (
                                <Col key={directory.id} xs={24} md={12}>
                                    <div
                                        style={{
                                            border: `1px solid ${token.colorBorder}`,
                                            borderRadius: token.borderRadius,
                                            padding: '0.875rem',
                                        }}
                                    >
                                        <Row align='top' gutter={12} wrap={false}>
                                            <Col flex='none'>
                                                <Avatar
                                                    icon={<TbSitemap size='1rem' />}
                                                    shape='square'
                                                    size={24}
                                                    style={{
                                                        background: token.colorBgElevated,
                                                        border: `1px solid ${token.colorBorder}`,
                                                        color: token.colorTextTertiary,
                                                    }}
                                                />
                                            </Col>
                                            <Col flex='auto'>
                                                <Typography.Text strong>
                                                    {directory.name || directory.domain}
                                                </Typography.Text>
                                                <div>
                                                    <small style={{ color: token.colorTextTertiary }}>
                                                        {directory.type || 'Directory'} · {directoryStateLabel(directory)}
                                                    </small>
                                                </div>
                                                {directory.updatedAt && (
                                                    <div>
                                                        <small style={{ color: token.colorTextTertiary }}>
                                                            Updated{' '}
                                                            {dayjs(directory.updatedAt).format('MMM D, YYYY h:mm A')}
                                                        </small>
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <EmptySection
                            height={16 * 14}
                            icon={<TbSitemap style={{ color: token.colorTextTertiary }} />}
                            label='No Directory Connected'
                            description='Open WorkOS Directory Sync to connect Okta, Microsoft Entra ID, Google Workspace, or another supported directory provider.'
                            extra={
                                <Button
                                    disabled={!isEnterprise}
                                    loading={openingPortalIntent === 'dsync'}
                                    onClick={() => openWorkOSPortal('dsync')}
                                >
                                    Configure Directory Sync
                                    <TbArrowUpRight
                                        style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                                    />
                                </Button>
                            }
                        />
                    )}
                </section>
            )}
        </>
    );
};

export default DirectorySyncSection;
